import React, { useEffect, useState } from "react";
import { createPatientApi } from "../../../../api-helpers/PatientApiHelper";
import { PatientExternalContact, PatientPersonalContact } from "patients-experience-client";
import OffCanvas from "../../../../components/offCanvas";
import { AddUpdateExternalCareTeamMembers } from "./AddUpdateExternalCareTeamMembers";
import { AddUpdatePersonal } from "./AddUpdatePersonal";
import ConfirmationBox from "../../../../components/ConfirmationBox";
import Spinner from "../../../../components/Spinner";
import Toaster, { ToastConfig } from "../../../../components/Toaster";
import { ErrorTypes } from "../../../../interfaces/errors";
import { ReactComponent as EditIcon } from "../../../../assets/img/EditTask.svg";
import PhoneLink from "../../../../components/PhoneLink";

interface ContactProps {
    patientId: string | undefined;
}

export const Contact: React.FC<ContactProps> = ({ patientId }) => {
    const patientApi = createPatientApi();
    const [externalCareTeamMembers, setExternalCareTeamMembers] = useState<PatientExternalContact[]>([])
    const [patientContactInfo, setPatientContactInfo] = useState<PatientPersonalContact[]>();
    const [offCanvasTitle, setOffCanvasTitle] = useState("");
    const [offCanvasComp, setOffCanvasComp] = useState("");
    const [offCanvasShow, setOffCanvasShow] = useState(false);
    const [selectedContact, setSelectedContact] = useState<PatientPersonalContact>({} as PatientPersonalContact)
    const [selectedExternalCareMember, setSelectedExternalCareMember] = useState<PatientExternalContact>({} as PatientExternalContact)
    const [loading, setloading] = useState(false);
    const [showConfirmBox, setShowConfirmBox] = useState<boolean>(false);
    const [confirmTitle, setConfirmTitle] = useState<string>('');
    const [confirmMessage, setConfirmMessage] = useState<string>('');
    const [showToast, setShowToast] = useState(false);
    const [toastConfig, setToastConfig] = useState<ToastConfig>({
        type: "success" as ErrorTypes,
        title: "",
        icon: "",
    });

    const PLACEHOLDER = <span className="placeholder-wave">
        <span className="placeholder w-100"></span>
    </span>;

    const getPatientContactInfo = async () => {
        try {
            const response = await patientApi.patientsControllerGetContacts(patientId!);
            
            setPatientContactInfo(response.data);
        } catch (error) {
            console.log(error);
        }
    };
    
    const getExternalCareTeamMembers = async () => {
        setloading(true);
        try {
            const response = await patientApi.patientsControllerGetExternalCareTeam(patientId!);
            
            setExternalCareTeamMembers(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setloading(false);
        }
    };

    useEffect(() => {
        if (patientId) {
            getPatientContactInfo();
            getExternalCareTeamMembers();
        }
    }, [patientId])

    const handleOffCanvasShow = (comp: string, contact?: any) => {
        if (comp === "editExternalCareTeamMembers") {
            setSelectedExternalCareMember(contact);
            setOffCanvasTitle("Edit External Care Team Member");
        }
        if (comp === "editPersonal") {
            setSelectedContact(contact)
            setOffCanvasTitle("Edit Contact Details");
        }
        if (comp === "addContact") {
            setOffCanvasTitle("Add Contact");
        }
        if (comp === "addExternalCareTeamMembers") {
            setOffCanvasTitle("Add External Care Team Member");
        }
        setOffCanvasComp(comp);
        setOffCanvasShow(!offCanvasShow);
    };

    const handleConfirm = async () => {
        setloading(true);
        try {
            if (selectedContact.contactId) {
                await patientApi.patientsControllerDeletePersonalContact(selectedContact.patientId, selectedContact.contactId)
                getPatientContactInfo();
            }
            if (selectedExternalCareMember.contactId) {
                await patientApi.patientsControllerDeleteExternalContact(selectedExternalCareMember.patientId, selectedExternalCareMember.contactId)
                getExternalCareTeamMembers();
            }
            showSuccessMessage('Record deleted successfully');
        } catch (error) {
            console.error(error);
            showErrorMessage('Error while deleting the record.');
        } finally {
            setloading(false);
            setShowConfirmBox(false);
        }
    }

    const showSuccessMessage = (message: string) => {
        setShowToast(false);
        const toasterConfig: ToastConfig = {
            type: "success",
            title: message,
            icon: "check_circle_outline",
        };
        setToastConfig(toasterConfig);
        setShowToast(true);
    }

    const showErrorMessage = (message: string) => {
        setShowToast(false);
        const toasterConfig: ToastConfig = {
            type: "danger",
            title: message,
            icon: "error_outline",
        };
        setToastConfig(toasterConfig);
        setShowToast(true);
    }

    const deleteContact = (contact: PatientPersonalContact) => {
        setShowConfirmBox(true);
        setConfirmTitle('Delete Contact');
        setConfirmMessage(`Are you sure you want to delete the contact '${contact?.contactCredential + ' ' + contact?.contactFirstName + ' ' + contact?.contactLastName}'? This action cannot be undone.`);
        setSelectedContact(contact);
        setSelectedExternalCareMember({} as PatientExternalContact);
    }

    const deleteTeamMember = (member: PatientExternalContact) => {
        setShowConfirmBox(true);
        setConfirmTitle('Delete External Care Team Member');
        setConfirmMessage(`Are you sure you want to delete the External care team member '${member?.contactCredential + ' ' + member?.contactFirstName + ' ' + member?.contactLastName}'? This action cannot be undone.`);
        setSelectedExternalCareMember(member);
        setSelectedContact({} as PatientPersonalContact);
    }

    return (<>
        {loading && <Spinner />}
        <div className="row">
            <Toaster
                show={showToast}
                onClose={() => setShowToast(false)}
                type={toastConfig.type}
                title={toastConfig.title}
                message={toastConfig.message}
                icon={toastConfig.icon}
            />
        </div>
        <div className='d-flex align-items-center'>
            <h2 className='h4 m-0'>Contact</h2>
        </div>
        <hr />
        <div>
            <h3 className="h5">Personal</h3>
            <div className="table-responsive">
            <table className="table border mt-4">
                <thead>
                    <tr>
                        <th scope="col" className="bg-teal-light">Preference</th>
                        <th scope="col" className="bg-teal-light">Relation</th>
                        <th scope="col" className="bg-teal-light">First Name</th>
                        <th scope="col" className="bg-teal-light">Last Name</th>
                        <th scope="col" className="bg-teal-light">Phone Type</th>
                        <th scope="col" className="bg-teal-light">Mobile</th>
                        <th scope="col" className="bg-teal-light">Email</th>
                        <th scope="col" className="bg-teal-light"></th>
                    </tr>
                </thead>
                <tbody>
                    {!loading && patientContactInfo?.length ? patientContactInfo.map((contact: PatientPersonalContact, index: number) => (<>
                        <tr>
                            <td>{contact.contactPreference?.toLowerCase() == 'p' ? 'Primary' : contact.contactPreference?.toLowerCase() == 's' ? 'Secondary' : 'Other'}</td>
                            <td className="text-titlecase">{contact.contactType.name}</td>
                            <td>{contact.contactFirstName}</td>
                            <td>{contact.contactLastName}</td>
                            <td>{contact.phoneType.name || ""}</td>
                            <td><PhoneLink phone={contact.phone} /></td>
                            <td>{contact.email}</td>
                            <td className="text-center">
                                <a href='javascript:void(0)' onClick={() => handleOffCanvasShow("editPersonal", contact)}>
                                    <EditIcon />
                                </a>
                                {contact.isSelfContact === false ? <a href='javascript:void(0)' onClick={() => deleteContact(contact)}>
                                    <span className='material-symbols-outlined fs-22 align-text-top ms-2 '>delete</span>
                                </a> : <span>
                                    <span className='material-symbols-outlined fs-22 align-text-top ms-2 invisible'>delete</span>
                                </span> }
                            </td>
                        </tr>
                    </>)) : ''}
                    {!loading && !patientContactInfo?.length &&
                        <tr>
                            <td className="text-center" colSpan={6}>No Data Found</td>
                        </tr>
                    }
                    {loading &&
                        Array.from({ length: 2 }).map((_) => (
                            <tr>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td className="text-center w-40px">{PLACEHOLDER}</td>
                            </tr>))
                    }
                </tbody>
            </table>
            </div>
            <a href='javascript:void(0)' className={`d-inline-block mb-5 border-primary border-bottom`} onClick={() => handleOffCanvasShow("addContact")}>
                <span className="lh-sm">Add</span>
                <span className="material-symbols-outlined fs-16 align-text-top me-0">add</span>
            </a>
        </div>
        <div>
            <h3 className="h5">External care team members</h3>
            <div className="table-responsive">
            <table className="table border mt-4">
                <thead>
                    <tr>
                        <th scope="col" className="bg-teal-light">Contact Person</th>
                        <th scope="col" className="bg-teal-light">Speciality</th>
                        <th scope="col" className="bg-teal-light">First Name</th>
                        <th scope="col" className="bg-teal-light">Last Name</th>
                        <th scope="col" className="bg-teal-light">Phone Type</th>
                        <th scope="col" className="bg-teal-light">Mobile</th>
                        <th scope="col" className="bg-teal-light">NPI</th>
                        <th scope="col" className="bg-teal-light"></th>
                    </tr>
                </thead>
                <tbody>
                    {!loading && externalCareTeamMembers?.length ? externalCareTeamMembers.map((member: PatientExternalContact, index: number) => (<>
                        <tr>
                            <td>{member.contactType.name}</td>
                            <td>{member.speciality.name}</td>
                            <td>{member.contactFirstName}</td>
                            <td>{member.contactLastName}</td>
                            <td>{member.phoneType.name || ""}</td>
                            <td><PhoneLink phone={member.phone} /></td>
                            <td>{member.npi}</td>
                            <td className="text-end">
                                {member.contactType.name.toLocaleLowerCase() !== 'provider' ? <a href='javascript:void(0)' onClick={() => handleOffCanvasShow("editExternalCareTeamMembers", member)}>
                                    <EditIcon />
                                </a> : ''}
                                <a href='javascript:void(0)' onClick={() => deleteTeamMember(member)}>
                                    <span className="material-symbols-outlined fs-22 align-text-top ms-2">delete</span>
                                </a>
                            </td>
                        </tr>
                    </>)) : ''}
                    {!loading && !externalCareTeamMembers?.length &&
                        <tr>
                            <td className="text-center" colSpan={7}>No Data Found</td>
                        </tr>
                    }
                    {loading &&
                        Array.from({ length: 2 }).map((_) => (
                            <tr>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td>{PLACEHOLDER}</td>
                                <td className="text-center w-40px">{PLACEHOLDER}</td>
                            </tr>))
                    }
                </tbody>
            </table>
            </div>
            <a href='javascript:void(0)' className={`d-inline-block border-primary border-bottom`} onClick={() => handleOffCanvasShow("addExternalCareTeamMembers")}>
                <span className="lh-sm">Add</span>
                <span className="material-symbols-outlined fs-16 align-text-top me-0">add</span>
            </a>
        </div>

        <OffCanvas
            title={offCanvasTitle}
            offCanvasShow={offCanvasShow}
            setOffCanvasShow={setOffCanvasShow}
            offCanvasComp={offCanvasComp}
        >
            {offCanvasComp === "editExternalCareTeamMembers" && (
                <AddUpdateExternalCareTeamMembers patientId={patientId} getExternalCareTeamMembers={getExternalCareTeamMembers} selectedExternalCareMember={selectedExternalCareMember} setOffCanvasShow={setOffCanvasShow} />
            )}
            {offCanvasComp === "addExternalCareTeamMembers" && (
                <AddUpdateExternalCareTeamMembers patientId={patientId} getExternalCareTeamMembers={getExternalCareTeamMembers} setOffCanvasShow={setOffCanvasShow} />
            )}
            {offCanvasComp === "editPersonal" && (
                <AddUpdatePersonal getPatientContactInfo={getPatientContactInfo} selectedContact={selectedContact} setOffCanvasShow={setOffCanvasShow} />
            )}
            {offCanvasComp === "addContact" && (
                <AddUpdatePersonal getPatientContactInfo={getPatientContactInfo} setOffCanvasShow={setOffCanvasShow} />
            )}
        </OffCanvas>

        <ConfirmationBox showConfirmBox={showConfirmBox} title={confirmTitle} message={confirmMessage} performAction={handleConfirm} closeConfirmBox={() => setShowConfirmBox(!showConfirmBox)} />
    </>);
}
